import cls from 'classnames';
import { useCallback, useRef, useEffect } from 'react';
import { Button, Overlay, Popover, CloseButton, Form } from 'react-bootstrap';
import AutoSizer from 'react-virtualized-auto-sizer';
import { FixedSizeList } from 'react-window';

import { EventPriority } from 'src/components/Trap/Elements';
import { useEffectState } from 'src/utils/hooks';
import { isValidPosition } from 'src/utils/helpers';

import classnames from './SidePanel.module.scss';

export const SidePanel = ({ items, selectedItem, editMode, onEditMode, onSubmit, onCancel, onSelectItem, onDropItem, canEditItem }) => {
  const listRef = useRef();
  const headerRef = useRef();
  const showPopover = useRef(true);
  const [popover, setPopover] = useEffectState(editMode);

  useEffect(() => { listRef.current && selectedItem && listRef.current.scrollToItem(selectedItem.index) }, [selectedItem, listRef.current]);

  function handleHidePopover() {
    showPopover.current = false;
    setPopover(false);
  }

  return (
    <div className="w-100 h-100 d-flex flex-column flex-nowrap">
      <div ref={headerRef} className='d-flex flex-row gap-col-3 p-3 pt-2 pb-3 justify-content-end align-items-center' >
        <div className="d-flex align-items-center gap-2">
          <span className="text-muted mb-0 mt-2">{__('Edit mode')}</span>
          <Form.Check
            type="switch"
            id="trapsmap-edit-switch"
            className="fs-4 m-0 align-self-center"
            checked={editMode}
            onChange={() => onEditMode(!editMode)}
            aria-label={__('Toggle edit mode')}
          />
        </div>
      </div>
      <div className="border-bottom" />
      <div className="w-100 h-100">
        <AutoSizer>
          {({ height, width }) => (
            <FixedSizeList ref={listRef} height={height} width={width} itemCount={items.length} itemSize={49}>
              {(({ index, style }) => {
                const item = items[index];
                const canEdit = typeof canEditItem === 'function' ? canEditItem(item) : true;

                return (
                  <ItemRow item={item} isSelected={item == selectedItem} editMode={editMode}
                    canEdit={canEdit} onSelect={onSelectItem} onDrop={onDropItem} style={style}
                  />
                );
              })}
            </FixedSizeList>
          )}
        </AutoSizer>
      </div>

      <Overlay target={headerRef.current} show={popover && showPopover.current} placement='bottom' rootClose onHide={handleHidePopover} >
        {(props) => (
          <Popover id="popover-basic" {...props} className="edit-mode-popover">
            <Popover.Header><CloseButton onClick={handleHidePopover} /></Popover.Header>
            <Popover.Body>{__('In order to place a trap onto a map, click on Arrow Down icon next to the trap name in the list above. A needle indicating trap position will then be placed in the middle of the map, and you can now drag and drop the needle. Use the zoom buttons to define a more precise placement of a trap.')}</Popover.Body>
          </Popover>
        )}
      </Overlay>

    </div >
  )
}

const ItemRow = ({ item, isSelected, editMode, canEdit, onSelect, onDrop, style }) => {
  const { trap_name, event_priority, changed, is_caught, event_name } = item;

  const handleItemClick = useCallback(() => onSelect(item), [item]);
  const handleDropClick = useCallback(() => editMode && canEdit && onDrop(item), [item, editMode, canEdit, onDrop]);

  return (
    <div className={cls(classnames.item, { [classnames.active]: isSelected })} style={style} onClick={handleItemClick} >
      <span className={classnames.icon}><EventPriority priority={event_priority} isCaught={is_caught} eventName={event_name} /></span>
      <span className={classnames.text}>
        {trap_name}
        {item?.serial_no && (
          <div className={cls(classnames.serial, "text-muted")}>{item.serial_no}</div>
        )}
      </span>
      {canEdit && !isValidPosition(changed) &&
        <div className={cls(classnames.location, { [classnames.active]: editMode })} onClick={handleDropClick} />
      }
    </div>
  )
}
